/**
 * @fileOverview The packets walkthrough: one packet (Fundament, a material packet) with a material
 * added to it, and the material catalogue's table it is listed in. Pure data.
 * See docs/view-help.md and docs/view-help/packets.md.
 */

import { ENTITY_ICONS } from "@/services/entityIcons"
import { PERMISSIONS } from "@/services/permission"
import { stepsOn } from "@/services/viewHelp/define"
import type { ViewHelpTour } from "@/services/viewHelp/types"

/** Reading packets — a packet's page needs it (`router/routes/packet.js`). */
const VIEW = [PERMISSIONS.VIEW_PACKET_GENERAL]

/** Changing packets — the create button and every control that edits. */
const EDIT = [PERMISSIONS.EDIT_PACKET_GENERAL]

/** The sample packet every detail step is read on — the world's material packet. */
const PACKET = { packetId: "pkt-fundament" }

const onDetail = stepsOn("PacketDetail", { permissions: VIEW, at: { params: PACKET } })

/** A project's Material register, and the customer's — both gated by their own pair of rights. */
const VIEW_PROJECT_MATERIAL = [PERMISSIONS.VIEW_ASSIGNED_PROJECT_MATERIAL, PERMISSIONS.VIEW_FOREIGN_PROJECT_MATERIAL]

// The related views: where a packet pays off, and where the records it bundles actually live. The
// pointer walks there (`services/viewHelp/ways.ts`); `at` says where to load the frame if that walk ever fails.
// Projekt Süd rather than Nord, which every other tour shows: Nord already carries all three materials
// of Fundament, and the Material register's picker drops a packet whose entries are all in the project.
// Süd has only Beton and Kies, so Fundament is offered there.
const onProject = stepsOn("ProjectDetail", {
  permissions: VIEW_PROJECT_MATERIAL,
  at: { params: { projectId: "p-sued" } },
})
// Muster AG rather than the world's Beispiel GmbH, which every other tour shows: Beispiel already has
// a price for both Zusatzleistungen of the sample packet, and the assign dialog drops a packet whose
// records are all assigned already. Muster has none of them, so the packet is in the list to be seen.
const onCustomer = stepsOn("CustomerDetail", {
  permissions: [PERMISSIONS.EDIT_CUSTOMER_ADDITIONAL_EFFORT],
  at: { params: { customerId: "c-muster" } },
})
// Where packets are listed: the material catalogue's table, with Fundament above the materials. The
// machine and additional effort catalogues list their packets the same way.
const onMaterials = stepsOn("MaterialOverview", { permissions: [PERMISSIONS.VIEW_MATERIAL_GENERAL] })

/**
 * Packets: bundles of catalogue records that a project (or a customer's price list) takes in one go.
 *
 * Fundament's own page first, where a material is picked into the packet and Speichern is pointed
 * at, never pressed. The three create forms have no steps: they are the same page with another
 * catalogue behind it.
 *
 * A packet holds nothing but a name and a list of ids, so the walkthrough spends its length on the one
 * thing a reader cannot see on the page — what the bundle is *for*, and where it is kept. That is the
 * related chapter: the catalogue's table, which lists the packet above the records it bundles, with
 * the filter that narrows to either and the button that creates one; the project register a packet
 * fills; and the customer's assign dialog where a packet is offered above the catalogue.
 */
export const PACKETS_TOUR: ViewHelpTour = {
  key: "packets",
  icon: ENTITY_ICONS.packet,
  // Packets have no list of their own: they are rows of their catalogue's table, which belongs to that
  // catalogue's tour and is visited here as a related view. The create forms have no steps; their
  // trigger opens the tour on the packet's page.
  routes: ["PacketDetail", "PacketCreateMaterial", "PacketCreateMachine", "PacketCreateAdditionalEffort"],
  // Where a packet is listed, where it is used. Played last, after a rule on the timeline — they are
  // other features' pages.
  related: ["MaterialOverview", "ProjectDetail", "CustomerDetail"],
  scenes: [
    // The catalogue's one create button, opened to show what it offers — a record of the catalogue or a
    // packet of it — and closed again with Escape. Neither entry is pressed: both leave the list for a
    // create form.
    { key: "create-menu", enter: [{ do: "click", target: "catalog-create" }], exit: [{ do: "escape" }] },
    // Kies picked into Fundament, and everything after it on the page read with Kies in the table.
    // Nothing to undo when the walkthrough moves on: leaving the page throws the unsaved pick away
    // (`endsWithView`). Going *back* on the page is the one time the exit runs — the scene then starts
    // over, and Kies has to leave the list first, or the picker would no longer offer it
    // (`filterExistentItems` in `LewAutocompletePickerMaterial`). The bin is named by its record, so
    // the press can only ever take out the pick, never one of the packet's own entries. It is the bin
    // itself rather than the row around it: `clickableIn` answers a `tr` with the row, which has no
    // handler, so a press there would do nothing at all.
    {
      key: "detail-add",
      enter: [],
      exit: [{ do: "click", target: "packet-item-remove-mat-kies" }],
      endsWithView: true,
    },
    // The project's Material register. The tab press is the step's own action, so a jump straight onto
    // it presses the tab where the reader can see it; nothing to put back, since leaving the page takes
    // the open register with it.
    { key: "project-materials", enter: [], exit: [], endsWithView: true },
    // The register's picker, searched for the packet and closed with Escape — it is a plain `v-dialog`
    // with no Abbrechen of its own. The packet is never picked: a pick takes every material the
    // customer has no price for into the customer's own price list at once, which is a write.
    // It opens its register's tab itself (`openTab`, so not a second time when the step before has just
    // done it): a scene may not count on another scene's state — a jump straight onto this step, or a
    // reader overtaking the register step, pressed "Hinzufügen" on a tab that was not open.
    {
      key: "project-packet",
      enter: [
        { do: "openTab", target: "project-tab-materials" },
        { do: "click", target: "project-material-add" },
      ],
      exit: [{ do: "escape" }],
    },
    // The customer's Zusatzleistungen register with its assign dialog open — the one window in the app
    // where a packet can be seen being offered. Always left by Abbrechen, and the tab press in `enter`
    // is what makes the dialog reachable from anywhere on the timeline.
    {
      key: "customer-assign",
      enter: [
        { do: "openTab", target: "customer-tab-additionalEfforts" },
        { do: "click", target: "customer-effort-assign" },
      ],
      exit: [{ do: "click", target: "customer-assign-cancel" }],
    },
  ],
  steps: [
    // ── Fundament ────────────────────────────────────────────────────────────
    // The card's head belongs to `LewEntityPage`, not to the packet's view, which is why it carries
    // its own anchor. The kind is the one thing on a packet's page that can never be changed, so it
    // is worth a step of its own before the fields under it.
    onDetail("detail-head", "entity-head", "mdi-package-variant-closed", { placement: "bottom" }),
    onDetail("detail-name", "packet-name", "mdi-form-textbox", { placement: "bottom" }),
    // A material picked into the packet, so the list under the field is seen growing by one. The names
    // are literal: a sample record is called the same in every language. Kies, because Fundament does
    // not hold it yet.
    onDetail("detail-items", "packet-items", "mdi-magnify-plus-outline", {
      placement: "top",
      scene: "detail-add",
      permissions: EDIT,
      actions: [
        { do: "type", target: "packet-items", text: "Kies" },
        { do: "choose", text: "Kies 0/32" },
      ],
    }),
    // The row, not the bin on it: the dot sits where the reader is looking anyway, and a step zoomed
    // onto a 32px icon button says less than the row it belongs to. Pointed at, never pressed — the
    // scene's exit presses the bin by its own anchor.
    onDetail("detail-row", "packet-item-first", "mdi-delete-outline", {
      placement: "bottom",
      scene: "detail-add",
      permissions: EDIT,
    }),
    // On the button itself, explained and never pressed.
    onDetail("detail-save", "packet-save", "mdi-content-save-outline", {
      placement: "top",
      scene: "detail-add",
      permissions: EDIT,
    }),

    // ── Where to find this again ─────────────────────────────────────────────
    onDetail("again", "view-help-trigger", "mdi-help-circle-outline", { placement: "bottom", scene: "detail-add" }),

    // ── Wo Pakete wirken ─────────────────────────────────────────────────────
    //
    // Not a tour of three more pages: a packet is a name and a list of ids, and everything it is good
    // for happens somewhere else. So each step names the place and what the packet does to it — the
    // catalogue that lists it, the register a packet fills, and the dialog that offers one.
    //
    // The catalogue's table first: the packet's row above the materials, the filter that narrows to
    // either, the button that creates a packet, and a material the packet bundles. The filter and the
    // button are pointed at, not pressed — the one would change the list under the steps after it, the
    // other leave the page. The page has already decided the kind, which is the one thing about a
    // packet that can never change.
    onMaterials("row", "row-pkt-fundament", ENTITY_ICONS.packet, { placement: "bottom", permissions: VIEW }),
    onMaterials("filter", "catalog-kind-filter", "mdi-filter-variant", { placement: "bottom" }),
    onMaterials("create", "packet-create", "mdi-plus", {
      placement: "bottom",
      scene: "create-menu",
      permissions: EDIT,
    }),
    onMaterials("catalogue", "row-mat-beton", ENTITY_ICONS.material, { placement: "bottom" }),
    //
    // No zoom: the pointer presses the tab and the register slides in underneath, which zoomed would
    // be out of the picture. The region holds the register *and* its Hinzufügen button, which is what
    // the text is about.
    onProject("project-register", "project-materials", ENTITY_ICONS.material, {
      placement: "bottom",
      scene: "project-materials",
      zoom: false,
      actions: [{ do: "openTab", target: "project-tab-materials" }],
    }),
    // Adding the packet: the picker opened by the scene, the packet's name typed, and the dot on the
    // packet's hit with its badge — pointed at, not pressed (see the scene).
    onProject("project-packet", "project-material-result-pkt-fundament", ENTITY_ICONS.packet, {
      placement: "right",
      scene: "project-packet",
      permissions: [PERMISSIONS.EDIT_ASSIGNED_PROJECT_MATERIAL, PERMISSIONS.EDIT_FOREIGN_PROJECT_MATERIAL],
      actions: [{ do: "type", target: "project-material-picker", text: "Fund" }],
    }),
    // The list of hits, where a packet stands above the catalogue with its badge and its count.
    onCustomer("customer-assign", "customer-assign-results", "mdi-plus-box-multiple-outline", {
      placement: "bottom",
      scene: "customer-assign",
    }),
  ],
}
